


import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import DiscoveryModel from '../pages/DiscoveryModel';
import JobCard from '../pages/JobCard';
import AggregatedJobCard from '../pages/AggregatedJobCard';


// const BACKEND_URL = 'http://127.0.0.1:8000';

const unwrap = (res) => (Array.isArray(res.data) ? res.data : (res.data?.data ?? []));

// SVG ring that wraps an avatar to show profile completion — same idea as a
// story-ring, sized to sit just outside the existing 64px avatar circle.
// Colour shifts by tier and a small badge overlays the bottom-right edge.
function AvatarRing({ percent, size = 76, stroke = 4, children }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, Math.max(0, percent)) / 100) * circumference;
  const color = percent === 100 ? '#10B981' : percent >= 60 ? '#7047FF' : '#F59E0B';

  return (
    <div className="relative mx-auto mb-3" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="absolute inset-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E5E7EB" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.4s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
      <span
        className="absolute -bottom-0.5 -right-0.5 text-[9px] font-bold text-white px-1.5 py-0.5 rounded-full border-2 border-white shadow-sm tabular-nums"
        style={{ backgroundColor: color }}
      >
        {percent}%
      </span>
    </div>
  );
}

// One accent per job type — reused for the card's left spine AND the type
// pill, so colour becomes a scannable key ("violet = full-time") instead of
// pure decoration.
const JOB_TYPE_ACCENT = {
  full_time: '#7047FF',
  part_time: '#DB2777',
  contract: '#7047FF',
  internship: '#F59E0B',
};
const accentFor = (type) => JOB_TYPE_ACCENT[type] || '#64748B';

// Resolves a usable <img> src from either a full URL or a relative storage path
const resolveLogoSrc = (logo) => {
  if (!logo) return null;
  return logo.startsWith('http') ? logo : `${BACKEND_URL}/storage/${logo}`;
};

const normalize = (s) => (s || '').toLowerCase().trim();

// Weighted match: skills 50%, preferred location 30%, role 20%.
// A criterion is skipped (and weights renormalized) when there's nothing
// to compare — e.g. the job lists no required skills.
const computeMatchDetails = (job, profileData) => {
  if (!profileData) return { percent: null, matchedSkills: [], locationMatch: false, matchedLocation: null, roleMatch: false };

  const profileSkills = (profileData.skills || []).map(normalize);
  const jobSkillsRaw = Array.isArray(job.skills_required)
    ? job.skills_required
    : Array.isArray(job.skills)
    ? job.skills
    : (job.skills_required || job.skills || '').split(',');
  const jobSkills = jobSkillsRaw.map((s) => s.trim()).filter(Boolean);
  const matchedSkills = jobSkills.filter((s) => profileSkills.includes(normalize(s)));
  const skillFraction = jobSkills.length > 0 ? matchedSkills.length / jobSkills.length : null;

  const jobLocation = normalize(job.location);
  const preferredLocations = (profileData.preferred_locations || []).map(normalize);
  let locationMatch = false;
  let matchedLocation = null;

  if (preferredLocations.length > 0) {
    matchedLocation = preferredLocations.find((loc) => loc && jobLocation.includes(loc));
    locationMatch = !!matchedLocation;
  } else {
    const city = normalize(profileData.city);
    const state = normalize(profileData.state);
    if (city && jobLocation.includes(city)) { locationMatch = true; matchedLocation = profileData.city; }
    else if (state && jobLocation.includes(state)) { locationMatch = true; matchedLocation = profileData.state; }
  }

  const pastTitles = (profileData.experience || []).map((e) => normalize(e.job_title));
  const objective = normalize(profileData.career_objective);
  const jobTitle = normalize(job.title);
  const hasRoleSignal = pastTitles.length > 0 || objective.length > 0;
  const roleMatch =
    pastTitles.some((t) => t && (jobTitle.includes(t) || t.includes(jobTitle))) ||
    (objective.length > 0 && jobTitle.split(' ').some((w) => w.length > 3 && objective.includes(w)));

  let totalWeight = 0;
  let scoreSum = 0;

  if (skillFraction !== null) { totalWeight += 50; scoreSum += 50 * skillFraction; }
  if (jobLocation) { totalWeight += 30; scoreSum += 30 * (locationMatch ? 1 : 0); }
  if (hasRoleSignal) { totalWeight += 20; scoreSum += 20 * (roleMatch ? 1 : 0); }

  const percent = totalWeight > 0 ? Math.round((scoreSum / totalWeight) * 100) : 0;

  return { percent, matchedSkills, locationMatch, matchedLocation, roleMatch };
};


function JobSearch() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
const [hasResume, setHasResume] = useState(null);
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  // "jobs" now holds a MERGED, tagged feed: each item is either a regular
  // JobPosting ({ ..._type: 'regular' }) or an AggregatedJob
  // ({ ..._type: 'aggregated' }). _key is a collision-safe React key,
  // since job_postings.id and aggregated_jobs.id both start at 1
  // independently (e.g. a regular job #3 and an aggregated job #3 both
  // exist) — plain `job.id` is NOT safe to use as a key here.
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    keyword: searchParams.get('keyword') || '',
    location: searchParams.get('location') || '',
    job_type: searchParams.get('job_type') || '',
    work_mode: searchParams.get('work_mode') || '',
    work_shift: searchParams.get('work_shift') || '',
        department: searchParams.get('department') || '',
    experience: searchParams.get('experience') || '',
    sort_by: searchParams.get('sort_by') || 'match',
  });

  const [expandedKey, setExpandedKey] = useState(null);
  const [applyingId, setApplyingId] = useState(null);
  const [popup, setPopup] = useState(null); // { type: 'limit' | 'error', title, message }
  const [appliedIds, setAppliedIds] = useState([]);
  const [profileCompletion, setProfileCompletion] = useState(null); // null while loading/unavailable
  const [matchScores, setMatchScores] = useState({}); // { [_key]: score }
  const [scoring, setScoring] = useState(false);
  const [profileData, setProfileData] = useState(null); // raw profile+skills+etc for scoring payload
  const [discoveryOpen, setDiscoveryOpen] = useState(false); // ← Discover Jobs modal toggle
const [profilePhotoPath, setProfilePhotoPath] = useState(user?.profile_photo_path || null);
const [keywordSuggestions, setKeywordSuggestions] = useState([]);
const [keywordLoading, setKeywordLoading] = useState(false);
const [showKeywordSuggestions, setShowKeywordSuggestions] = useState(false);
const keywordDebounceRef = useRef(null);
const [departments, setDepartments] = useState([]);
const [departmentSearch, setDepartmentSearch] = useState('');

const [locationSuggestions, setLocationSuggestions] = useState([]);
const [locationLoading, setLocationLoading] = useState(false);
const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
const locationDebounceRef = useRef(null);



  // Mobile: filters are collapsed behind a toggle below the lg breakpoint
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
  const prevTitle = document.title;
  document.title = 'Search Jobs in India – Latest Vacancies | PlanB Jobs';

  let meta = document.querySelector('meta[name="description"]');
  const created = !meta;
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'description';
    document.head.appendChild(meta);
  }
  const prevDesc = meta.getAttribute('content');
  meta.setAttribute(
    'content',
    'Search the latest jobs in India by location, skills, salary, experience and industry. Discover fresher, experienced and remote opportunities on PlanB Jobs.'
  );

  return () => {
    document.title = prevTitle;
    if (created) meta.remove();
    else if (prevDesc !== null) meta.setAttribute('content', prevDesc);
  };
}, []);

  useEffect(() => {
    const fetchProfileCompletion = async () => {
      if (!user || user.role !== 'job_seeker') return;
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const [profileRes, skillsRes, eduRes, expRes, certRes, langRes] = await Promise.all([
          api.get('/profile'),
          api.get('/skills'),
          api.get('/educations'),
          api.get('/experiences'),
          api.get('/certifications'),
          api.get('/languages'),
        ]);

        setProfileData({
          career_objective: profileRes.data.career_objective,
          city: profileRes.data.city,
          state: profileRes.data.state,
          skills: unwrap(skillsRes).map(s => s.name),
          education: unwrap(eduRes),
          experience: unwrap(expRes),
          certifications: unwrap(certRes).map(c => c.name),
          languages: unwrap(langRes).map(l => l.name),
            preferred_locations: profileRes.data.preferred_locations || [],
        });

       const p = profileRes.data;
setHasResume(!!p.resume_path);
setProfilePhotoPath(p.profile_photo_path || null);
        
        const personalFields = [
          p.name, p.phone, p.date_of_birth, p.gender,
          p.address, p.city, p.state, p.pincode, p.career_objective,
        ];
        const personalFraction = personalFields.filter(Boolean).length / personalFields.length;
        const documentsFraction = [!!p.resume_path, !!p.profile_photo_path].filter(Boolean).length / 2;

        const fractions = [
          personalFraction,
          documentsFraction,
          p.aadhaar_status === 'verified' ? 1 : 0,
          unwrap(skillsRes).length > 0 ? 1 : 0,
          unwrap(eduRes).length > 0 ? 1 : 0,
          unwrap(expRes).length > 0 ? 1 : 0,
          unwrap(certRes).length > 0 ? 1 : 0,
          unwrap(langRes).length > 0 ? 1 : 0,
        ];
        const overall = Math.round((fractions.reduce((sum, f) => sum + f, 0) / fractions.length) * 100);
        setProfileCompletion(overall);
      } catch (err) {
        console.error('Failed to compute profile completion', err);
      }
    };
    fetchProfileCompletion();
  }, [user]);

  useEffect(() => {
  const fetchDepartments = async () => {
    try {
      const res = await api.get('/job-categories');
      setDepartments(res.data || []);
    } catch (err) {
      console.error('Failed to fetch departments', err);
    }
  };
  fetchDepartments();
}, []);

  useEffect(() => {
    const fetchAppliedJobs = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await api.get('/my-applications');
        // A withdrawn application for a job should not count as "already applied"
        // -- exclude it so the job still shows an Apply button.
        const ids = res.data
          .filter((application) => application.status !== 'withdrawn')
          .map((application) => application.job_posting_id);
        setAppliedIds(ids.filter(Boolean));
      } catch (err) {
        console.error('Failed to fetch applied jobs', err);
      }
    };
    fetchAppliedJobs();
  }, []);

    const fetchJobs = async (override = {}) => {
    const f = { ...filters, ...override };
    setLoading(true);
    try {
      const params = {};
      Object.keys(f).forEach((key) => {
        if (f[key]) params[key] = f[key];
      });

      if (user?.role === 'employer') {
        params.employer_id = user.id;
      }

      const aggregatedFiltersApply = !f.work_mode && !f.work_shift;

      const aggParams = {};
      if (f.keyword) aggParams.keyword = f.keyword;
      if (f.location) aggParams.location = f.location;
      if (f.department) aggParams.category = f.department;
            if (f.experience) aggParams.experience = f.experience;


      const requests = [api.get('/jobs/search', { params })];
      if (aggregatedFiltersApply && user?.role !== 'employer') {
        requests.push(api.get('/aggregated-jobs', { params: aggParams }));
      }

      const [regularRes, aggRes] = await Promise.all(requests);

      const regularJobs = (regularRes.data || []).map((j) => ({
        ...j,
        _type: 'regular',
        _key: `reg-${j.id}`,
      }));

      const aggregatedJobs = (aggRes?.data || []).map((j) => ({
        ...j,
        _type: 'aggregated',
        _key: `agg-${j.id}`,
      }));

      setJobs([...regularJobs, ...aggregatedJobs]);
    } catch (err) {
      console.error('Failed to fetch jobs', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.job_type, filters.work_mode, filters.work_shift, filters.department, filters.sort_by]);

  const isFirstUrlRun = useRef(true);

    useEffect(() => {
    // Skip the first run, the effect above already loads jobs on page open
    if (isFirstUrlRun.current) {
      isFirstUrlRun.current = false;
      return;
    }

    const fromUrl = {
      keyword: searchParams.get('keyword') || '',
      location: searchParams.get('location') || '',
      department: searchParams.get('department') || '',
       experience: searchParams.get('experience') || '',
      job_type: searchParams.get('job_type') || '',
      work_mode: searchParams.get('work_mode') || '',
      work_shift: searchParams.get('work_shift') || '',
    };

    setFilters((f) => ({ ...f, ...fromUrl }));
    fetchJobs(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleFilterChange = (e) => {
    const updated = { ...filters, [e.target.name]: e.target.value };
    setFilters(updated);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs();
    setShowFilters(false); // collapse the drawer on mobile after searching
  };

    const fetchKeywordSuggestions = async (query) => {
    if (!query.trim() || query.trim().length < 2) {
      setKeywordSuggestions([]);
      return;
    }
    setKeywordLoading(true);
    try {
      const res = await api.post('/ai/suggest-keywords', { query: query.trim() });
      setKeywordSuggestions(res.data.suggestions || []);
    } catch (err) {
      setKeywordSuggestions([]);
    } finally {
      setKeywordLoading(false);
    }
  };

  const fetchLocationSuggestions = async (query) => {
    if (!query.trim() || query.trim().length < 2) {
      setLocationSuggestions([]);
      return;
    }
    setLocationLoading(true);
    try {
      const res = await api.post('/ai/suggest-cities', { query: query.trim() });
      setLocationSuggestions(res.data.suggestions || []);
    } catch (err) {
      setLocationSuggestions([]);
    } finally {
      setLocationLoading(false);
    }
  };

  const handleKeywordChange = (e) => {
    const value = e.target.value;
    setFilters((f) => ({ ...f, keyword: value }));
    setShowKeywordSuggestions(true);
    if (keywordDebounceRef.current) clearTimeout(keywordDebounceRef.current);
    keywordDebounceRef.current = setTimeout(() => fetchKeywordSuggestions(value), 350);
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setFilters((f) => ({ ...f, location: value }));
    setShowLocationSuggestions(true);
    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
    locationDebounceRef.current = setTimeout(() => fetchLocationSuggestions(value), 350);
  };

  const selectKeyword = (v) => {
    setFilters((f) => ({ ...f, keyword: v }));
    setKeywordSuggestions([]);
    setShowKeywordSuggestions(false);
  };

  const selectLocation = (v) => {
    const cityOnly = v.split(',')[0].trim();
    setFilters((f) => ({ ...f, location: cityOnly }));
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
  };

  useEffect(() => {
    return () => {
      if (keywordDebounceRef.current) clearTimeout(keywordDebounceRef.current);
      if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
    };
  }, []);

    // Discourage casual copying of job listings on this page.
  // NOTE: this is a UI-level deterrent only — it can be bypassed via
  // browser dev tools, disabling JS, or the API directly. It stops
  // casual right-click/copy, not determined scraping.
  useEffect(() => {
    const blockContextMenu = (e) => e.preventDefault();
    const blockCopy = (e) => e.preventDefault();
    const blockSelectStart = (e) => e.preventDefault();

    document.addEventListener('contextmenu', blockContextMenu);
    document.addEventListener('copy', blockCopy);
    document.addEventListener('selectstart', blockSelectStart);

    return () => {
      document.removeEventListener('contextmenu', blockContextMenu);
      document.removeEventListener('copy', blockCopy);
      document.removeEventListener('selectstart', blockSelectStart);
    };
  }, []);

  useEffect(() => {
    const fetchMatchScores = async () => {
      if (!user || user.role !== 'job_seeker' || !profileData || jobs.length === 0) return;

      const regularIds = jobs.filter((j) => j._type === 'regular').map((j) => j.id);
      const aggregatedIds = jobs.filter((j) => j._type === 'aggregated').map((j) => j.id);

      setScoring(true);
      try {
        const requests = [];
        if (regularIds.length > 0) {
          requests.push(api.post('/jobs/match-score', { jobs: regularIds }));
        }
        if (aggregatedIds.length > 0) {
          requests.push(api.post('/aggregated-jobs/match-score', { jobs: aggregatedIds }));
        }

        const results = await Promise.all(requests);

        // Merge both score maps back into the same _key space used by
        // `jobs`, so lookups stay consistent regardless of which endpoint
        // a given score came from.
        const map = {};
        let resultIndex = 0;
        if (regularIds.length > 0) {
          results[resultIndex].data.forEach((s) => { map[`reg-${s.id}`] = s.score; });
          resultIndex++;
        }
        if (aggregatedIds.length > 0) {
          results[resultIndex].data.forEach((s) => { map[`agg-${s.id}`] = s.score; });
        }
        setMatchScores(map);
      } catch (err) {
        console.error('Match scoring failed', err);
      } finally {
        setScoring(false);
      }
    };
    fetchMatchScores();
  }, [jobs, profileData, user]);

 const handleQuickApply = async (e, jobId) => {
  e.preventDefault();
  e.stopPropagation();

  const token = localStorage.getItem('token');
  if (!token) {
    window.location.href = '/login';
    return;
  }

  // No resume on profile -> send to job detail page to upload one
  if (!hasResume) {
    navigate(`/jobs/${jobId}`);
    return;
  }

  setApplyingId(jobId);
  try {
    await api.post(`/jobs/${jobId}/apply`, {});
    setAppliedIds((prev) => [...prev, jobId]);
  } catch (err) {
    const msg = err.response?.data?.message || 'Failed to apply.';
    if (msg.toLowerCase().includes('already applied')) {
      setAppliedIds((prev) => [...prev, jobId]);
    } else if (msg.toLowerCase().includes('per day')) {
      setPopup({ type: 'limit', title: 'Daily limit reached', message: msg });
    } else {
      setPopup({ type: 'error', title: 'Could not apply', message: msg });
    }
  } finally {
    setApplyingId(null);
  }
};

  const jobMatches = jobs.reduce((acc, job) => {
    const bd = job.match_breakdown || {};
    acc[job._key] = {
      percent: job.match_score ?? null,
      matchedSkills: bd.skills?.matched || [],
      locationMatch: (bd.location?.score || 0) > 0,
      matchedLocation: null,
      // Pass the full nested breakdown through as-is (score/max/applicable
      // per category) rather than flattening to a fixed set of fields —
      // JobCard reads whichever categories are present, so adding new
      // scoring categories on the backend doesn't require touching this file.
      matchBreakdown: bd,
    };
    return acc;
  }, {});

const sortedJobs = filters.sort_by === 'match'
  ? [...jobs].sort((a, b) => (jobMatches[b._key]?.percent ?? -1) - (jobMatches[a._key]?.percent ?? -1))
  : jobs;

  return (
    <div className="min-h-screen bg-[#FAF5FF] select-none">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* <p className="text-gray-500 text-sm mb-4">
          {filters.location ? `Jobs near ${filters.location}` : 'Jobs across India'}
        </p> */}

                {/* <div className="mb-6">
          <h1 className="inline-block text-2xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-[#7C3AED] to-[#DB2777] bg-clip-text text-transparent">
  Find Jobs That Move Your Career Forward.
</h1>
          <p className="text-gray-500 mt-2 text-sm sm:text-base">
            Search smarter. Explore opportunities. Apply with confidence.
          </p>
        </div> */}

        {/* ← Discover Jobs button + job count on the same row */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          {user && user.role === 'job_seeker' && (
            <button
              onClick={() => setDiscoveryOpen(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 shadow-sm"
            >
                          ✨ Discover Jobs For You
            </button>
          )}
          <span className="text-xl sm:text-2xl font-bold text-[#252438]">
            {jobs.length} jobs found
          </span>
        </div>


        {scoring && (
          <p className="text-xs text-[#7047FF] mb-4">Scoring jobs against your profile…</p>
        )}

        {/* Mobile-only filter toggle */}
        <button
          onClick={() => setShowFilters((s) => !s)}
          className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#252438]"
        >
          <span>🔍 Filters</span>
          <span className="text-sm text-gray-400">{showFilters ? 'Hide ▲' : 'Show ▼'}</span>
        </button>

        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* Filters Sidebar */}
          <div className="w-full lg:w-64 shrink-0 lg:sticky lg:top-8 lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto">
  <div className={`${showFilters ? 'block' : 'hidden'} lg:block bg-white rounded-xl shadow p-5`}>
              <form onSubmit={handleSearch} className="space-y-4">
                               <div className="relative">
                  <label className="text-xs font-bold text-gray-500 uppercase">Job Title</label>
                  <input
                    type="text"
                    name="keyword"
                    placeholder="Job title or keywords"
                    value={filters.keyword}
                    onChange={handleKeywordChange}
                    onFocus={() => setShowKeywordSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowKeywordSuggestions(false), 150)}
                    autoComplete="off"
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#7047FF] focus:border-transparent"
                  />
                  {showKeywordSuggestions && (keywordSuggestions.length > 0 || keywordLoading) && (
                    <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                      {keywordLoading ? (
                        <p className="text-xs text-gray-400 px-3 py-2">Searching…</p>
                      ) : (
                        keywordSuggestions.map((s) => (
                          <button
                            key={s}
                            type="button"
                            onMouseDown={() => selectKeyword(s)}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-[#7047FF]"
                          >
                            {s}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <label className="text-xs font-bold text-gray-500 uppercase">Location</label>
                  <input
                    type="text"
                    name="location"
                    placeholder="City"
                    value={filters.location}
                    onChange={handleLocationChange}
                    onFocus={() => setShowLocationSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 150)}
                    autoComplete="off"
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#7047FF] focus:border-transparent"
                  />
                  {showLocationSuggestions && (locationSuggestions.length > 0 || locationLoading) && (
                    <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                      {locationLoading ? (
                        <p className="text-xs text-gray-400 px-3 py-2">Searching…</p>
                      ) : (
                        locationSuggestions.map((s) => (
                          <button
                            key={s}
                            type="button"
                            onMouseDown={() => selectLocation(s)}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-[#7047FF]"
                          >
                            {s}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white py-2 rounded-lg font-semibold text-sm hover:opacity-90 shadow-sm"
                >
                  Apply Search
                </button>

                <hr />

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Job Type</label>
                  <div className="space-y-1">
                    {[
                      { value: '', label: 'All' },
                      { value: 'full_time', label: 'Full Time' },
                      { value: 'part_time', label: 'Part Time' },
                      { value: 'internship', label: 'Internship' },
                      { value: 'contract', label: 'Contract' },
                                       ].map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="job_type"
                          value={opt.value}
                          checked={filters.job_type === opt.value}
                          onChange={handleFilterChange}
                          className="accent-[#7047FF]"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <hr />

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Department</label>
                  <div className="relative mb-2">
                    <input
                      type="text"
                      placeholder="Search"
                      value={departmentSearch}
                      onChange={(e) => setDepartmentSearch(e.target.value)}
                      className="w-full border rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7047FF] focus:border-transparent"
                    />
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
                      <circle cx="11" cy="11" r="7" />
                      <path d="m21 21-4.3-4.3" />
                    </svg>
                  </div>
                  <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="department"
                        value=""
                        checked={filters.department === ''}
                        onChange={handleFilterChange}
                        className="accent-[#7047FF]"
                      />
                      All
                    </label>
                    {departments
                      .filter((dept) => dept.name.toLowerCase().includes(departmentSearch.trim().toLowerCase()))
                      .map((dept) => (
                        <label key={dept.slug} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="radio"
                            name="department"
                            value={dept.slug}
                            checked={filters.department === dept.slug}
                            onChange={handleFilterChange}
                            className="accent-[#7047FF]"
                          />
                          {dept.name}
                        </label>
                      ))}
                    {departments.length > 0 && departments.filter((d) => d.name.toLowerCase().includes(departmentSearch.trim().toLowerCase())).length === 0 && (
                      <p className="text-xs text-gray-400 px-1 py-2">No matching departments.</p>
                    )}
                  </div>
                </div>

                <hr />

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">
                    Work Mode <span className="normal-case font-normal text-gray-400">(regular jobs only)</span>
                  </label>
                  <div className="space-y-1">
                    {[
                      { value: '', label: 'All' },
                      { value: 'office', label: 'Work from Office' },
                      { value: 'hybrid', label: 'Hybrid' },
                      { value: 'work_from_home', label: 'Work from Home' },
                    ].map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="work_mode"
                          value={opt.value}
                          checked={filters.work_mode === opt.value}
                          onChange={handleFilterChange}
                          className="accent-[#7047FF]"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <hr />

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">
                    Work Shift <span className="normal-case font-normal text-gray-400">(regular jobs only)</span>
                  </label>
                  <div className="space-y-1">
                    {[
                      { value: '', label: 'All' },
                      { value: 'day', label: 'Day Shift' },
                      { value: 'night', label: 'Night Shift' },
                    ].map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="work_shift"
                          value={opt.value}
                          checked={filters.work_shift === opt.value}
                          onChange={handleFilterChange}
                          className="accent-[#7047FF]"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <hr />

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Sort By</label>
                  <div className="space-y-1">
                    {[
                      { value: '', label: 'Relevant' },
                      { value: 'match', label: 'Best Match for You' },
                      { value: 'salary_high', label: 'Salary - High to Low' },
                      { value: 'date_new', label: 'Date Posted - New to Old' },
                    ].map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="sort_by"
                          value={opt.value}
                          checked={filters.sort_by === opt.value}
                          onChange={handleFilterChange}
                          className="accent-[#7047FF]"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Job Listings */}
          <div className="flex-1 w-full space-y-4">
            {loading ? (
              <p className="text-gray-500">Loading jobs...</p>
            ) : jobs.length === 0 ? (
              <div className="bg-white rounded-xl shadow p-10 text-center">
                <p className="text-gray-600 mb-5">
                  Nothing here yet. Try another search and discover your next opportunity.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    const cleared = {
                      keyword: '', location: '', job_type: '', work_mode: '',
                      work_shift: '', department: '', sort_by: filters.sort_by,
                    };
                    setFilters(cleared);
                    fetchJobs(cleared);
                  }}
                  className="bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 shadow-sm"
                >
                  Explore All Jobs
                </button>
              </div>
            ) : (
             sortedJobs.map((job) => (
  job._type === 'aggregated' ? (
    <AggregatedJobCard
      key={job._key}
      job={job}
      matchScore={jobMatches[job._key]?.percent}
      matchReasons={jobMatches[job._key]}
      matchBreakdown={jobMatches[job._key]?.matchBreakdown}
      BACKEND_URL={BACKEND_URL}
    />
  ) : (
    <JobCard
      key={job._key}
      job={job}
      matchScore={jobMatches[job._key]?.percent}
      matchReasons={jobMatches[job._key]}
      matchBreakdown={jobMatches[job._key]?.matchBreakdown}
      applied={appliedIds.includes(job.id)}
      applying={applyingId === job.id}
      logoSrc={resolveLogoSrc(job.company_logo || job.employer?.company_logo)}
      onApply={(e) => handleQuickApply(e, job.id)}
      expanded={expandedKey === job._key}
      onToggle={() => setExpandedKey((k) => (k === job._key ? null : job._key))}
    />
  )
))
            )}
          </div>

          {/* Right Profile Card */}
          {user && user.role === 'job_seeker' && (
            <div className="w-full lg:w-72 shrink-0 space-y-4 lg:sticky lg:top-8">
              <div className="bg-white rounded-xl shadow p-6 text-center">
            {profileCompletion !== null ? (
  <AvatarRing percent={profileCompletion}>
    {profilePhotoPath ? (
      <img
        src={resolveLogoSrc(profilePhotoPath)}
        alt={user.name}
        className="w-16 h-16 rounded-full object-cover"
      />
    ) : (
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
          <circle cx="12" cy="8" r="3.4" />
          <path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" />
        </svg>
      </div>
    )}
  </AvatarRing>
) : profilePhotoPath ? (
  <img
    src={resolveLogoSrc(profilePhotoPath)}
    alt={user.name}
    className="w-16 h-16 rounded-full object-cover mx-auto mb-3"
  />
) : (
  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mx-auto mb-3">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" />
    </svg>
  </div>
)}
                <h4 className="font-bold text-[#252438]">{user.name}</h4>
                {profileCompletion !== null && (
                  <p className="text-xs text-gray-400 mt-1">
                    {profileCompletion === 100 ? 'Profile complete' : `Profile ${profileCompletion}% complete`}
                  </p>
                )}
                <Link
                  to="/profile"
                  className="inline-block mt-3 border border-[#7047FF] text-[#7047FF] px-4 py-1.5 rounded-lg text-sm font-semibold hover:bg-purple-50"
                >
                  Update Profile
                </Link>
              </div>
              <div className="bg-white rounded-xl shadow p-6">
                <h4 className="font-bold text-[#252438] mb-2">Quick Links</h4>
                <div className="space-y-2 text-sm">
                  <Link to="/my-applications" className="block text-gray-600 hover:text-[#7047FF]">
                    📄 My Applications
                  </Link>
                  <Link to="/resume-builder" className="block text-gray-600 hover:text-[#7047FF]">
                    📝 Resume Builder
                  </Link>
                  <Link to="/saved-jobs" className="block text-gray-600 hover:text-[#7047FF]">
                    ⭐ Saved Jobs
                   </Link>
                                    <Link to="/helpsupport" className="block text-gray-600 hover:text-[#7047FF]">
                    ❓ Help & Support
                   </Link>
                </div>
              </div>
            </div>
          )}

          {/* Right card for logged-out visitors */}
          {!user && (
            <div className="w-full lg:w-72 shrink-0 space-y-4 lg:sticky lg:top-8">
              <div className="bg-white rounded-xl shadow p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mx-auto mb-3">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                    <circle cx="12" cy="8" r="3.4" />
                    <path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" />
                  </svg>
                </div>
                <h4 className="font-bold text-[#252438]">Welcome to PlanB Jobs</h4>
                <p className="text-xs text-gray-400 mt-1">Login to apply and see your match score</p>
                <div className="flex flex-col gap-2 mt-4">
                  <Link to="/login" className="w-full bg-pb-purple text-white py-2 rounded-lg text-sm font-semibold hover:opacity-90 shadow-sm">
                    Login
                  </Link>
                  <Link to="/register" className="w-full border border-[#7047FF] text-[#7047FF] py-2 rounded-lg text-sm font-semibold hover:bg-purple-50">
                    Register
                  </Link>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow p-6">
                <h4 className="font-bold text-[#252438] mb-2">Quick Links</h4>
                <div className="space-y-2 text-sm">
                  <Link to="/login" className="block text-gray-600 hover:text-[#7047FF]">📄 My Applications</Link>
                  <Link to="/login" className="block text-gray-600 hover:text-[#7047FF]">📝 Resume Builder</Link>
                  <Link to="/login" className="block text-gray-600 hover:text-[#7047FF]">⭐ Saved Jobs</Link>
                  <Link to="/login" className="block text-gray-600 hover:text-[#7047FF]">❓ Help & Support</Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---- Apply result popup ---- */}
      {popup && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-[2px] p-4"
          onClick={() => setPopup(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-pb-purple px-6 pt-8 pb-6 text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-white/20 flex items-center justify-center text-3xl">
                {popup.type === 'limit' ? '⏳' : '⚠️'}
              </div>
              <h3 className="text-xl font-bold text-white mt-4">{popup.title}</h3>
            </div>

            <div className="px-6 py-5 text-center">
              <p className="text-sm text-gray-600 leading-relaxed">{popup.message}</p>

              {popup.type === 'limit' && (
                <div className="mt-4 bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 text-xs text-purple-700">
                  Your limit resets tomorrow. Meanwhile, you can review the jobs you already applied to.
                </div>
              )}

              <div className="flex gap-3 mt-6">
                {popup.type === 'limit' && (
                  <Link
                    to="/my-applications"
                    className="flex-1 border border-[#7047FF] text-[#7047FF] py-2.5 rounded-xl text-sm font-semibold hover:bg-purple-50"
                  >
                    My Applications
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => setPopup(null)}
                  className="flex-1 bg-pb-purple text-white py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 shadow-sm"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      

      {/* ← Discovery modal */}
      <DiscoveryModel
        isOpen={discoveryOpen}
        onClose={() => setDiscoveryOpen(false)}
      />
    </div>
  );
}

export default JobSearch;